import * as React from 'react';
import { createUseStyles } from 'react-jss';
import { animated, useSpring } from 'react-spring';

import ProductOrderForm from './ProductOrderForm';
import Button from '../components/Buttons/Button';
import ProductCharacteristics from './ProductCharacteristics';
import { useAppData, useIntl } from '../providers';
import { ProductDownloads } from './index';
import { useState, useEffect } from "react";

const useStyles = createUseStyles({
    productSheet: {
        padding: '2rem',
        backgroundColor: '#e6e6e6',
        display: 'flex',
        flexDirection: 'column',
    },
    header: {
        flex: 0,
        '& h1': {
            fontSize: '2rem',
            marginRight: '150px',
            color: 'var(--primary-color)',
            fontWeight: 'bold',
        },
    },
    description: {
        margin: '1rem 0',
        maxWidth: '90%',
    },
    formWrap: {
        margin: '1rem 0',
    },
    selectOrderModeWrap: {
        padding: '1rem 0',
        display: 'flex',
        flexWrap: 'wrap',
    },
    selectOrderModeButtonOrder: {
        flex: '1',
        margin: '0 1rem',
        padding: '1rem 0',
        color: 'white',
        backgroundColor: 'var(--secondary-color)',
    },
    selectOrderModeButtonInquiry: {
        flex: '1',
        margin: '0 1rem',
        padding: '1rem 0',
    },
    logo: {
        '& img': {
            maxWidth: '100px',
            maxHeight: '100px',
            height: 'auto',
            '@media (min-width: 640px)': {
                maxWidth: '140px',
            },
            '@media (min-width: 1024px)': {
                maxHeight: '140px',
            },
        },
        '@media (min-width: 640px)': {
            position: 'absolute',
            right: '2rem',
            top: '2rem',
        },
    },
});

const ProductSheet: React.FC = () => {
    const classes = useStyles();
    const { translate } = useIntl();
    const { selectedProduct: product, orderModeSelectable } = useAppData();

    const [explicitOrderMode, setExplicitOrderMode] = useState(null);

    const showOrderModeButtons = orderModeSelectable && explicitOrderMode === null;

    const categoryListAnimation = useSpring({
        from: { opacity: 0, transform: `translateY(10%)` },
        to: { opacity: 1, transform: `translateY(0%)` },
        config: { mass: 5, tension: 500, friction: 80 },
    });

    const handleSelectOrderMode = (orderMode) => () => {
        setExplicitOrderMode(orderMode);
        setTimeout(() => {
            const formElement = document.querySelector(`.${classes.formWrap}`);
            if (formElement) {
                formElement.scrollIntoView({ behavior: 'smooth' });
            }
        }, 100);
    };

    useEffect(() => {
        // Check if there's a hash in the URL
        const hash = window.location.hash;
        if (hash) {
            // Remove the # from the hash
            const elementId = hash.replace('#', '');
            const element = document.getElementById(elementId);
            if (element) {
                // Add a small delay to ensure the component is fully rendered
                setTimeout(() => {
                    element.scrollIntoView({ behavior: 'smooth' });
                }, 100);
            }
        }
    }, [product]); // Re-run when product changes

    return product ? (
        <animated.article className={classes.productSheet} style={categoryListAnimation}>
            <header className={classes.header}>
                <h1>{product.label}</h1>
            </header>
            {product.logo && (
                <div className={classes.logo}>
                    <img src={product.logo} alt={product.logoAlternativeText} />
                </div>
            )}
            <ProductCharacteristics product={product} smallFont={false} allowPreview={true} />
            {product.description && (
                <div className={classes.description}>
                    <strong>{translate('productCard.header.description', 'Product information')}</strong>
                    <div dangerouslySetInnerHTML={{ __html: product.description }} />
                </div>
            )}
            {product.downloads.length > 0 && <ProductDownloads product={product} />}
            {showOrderModeButtons && (
                <div id="order" className={classes.selectOrderModeWrap}>
                    <Button
                        className={classes.selectOrderModeButtonInquiry}
                        label={translate('productCard.action.orderModeInquiry', 'Inquiry')}
                        onClick={handleSelectOrderMode(false)}
                    />
                    <Button
                        className={classes.selectOrderModeButtonOrder}
                        label={translate('productCard.action.orderModeOrder', 'Order')}
                        onClick={handleSelectOrderMode(true)}
                    />
                </div>
            )}
            {!showOrderModeButtons && (
                <div className={classes.formWrap}>
                    <ProductOrderForm product={product} explicitOrderMode={explicitOrderMode} />
                </div>
            )}
        </animated.article>
    ) : (
        <div />
    );
};

export default React.memo(ProductSheet);
